import { useEffect, useState } from "react";
import { LocalizationProvider, DateRangePicker } from "@mui/x-date-pickers-pro";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import type { Moment } from "moment";
import InsertInvitationIcon from "@mui/icons-material/InsertInvitation";
import { removeLicenseKeyWatermark } from "@/helpers/utils";

const CustomElements = ({
  onChange,
  initialVal,
  disableWeekSelection,
  isEvergreenCampaign,
}: any) => {
  const today = moment().startOf("isoWeek");
  const [value, setValue] = useState<[Moment, Moment]>([
    today,
    today.clone().add(4, "days"),
  ]);

  // Handle initial value
  useEffect(() => {
    if (initialVal) {
      const m = moment(initialVal);
      if (m.isValid()) {
        const start = m.startOf("isoWeek");
        const end = start.clone().add(4, "days");
        setValue([start, end]);
      }
    }
  }, [initialVal]);

  // Handle week change - automatically snap to Monday-Friday
  const handleWeekChange = (newValue: any) => {
    if (newValue && newValue[0]) {
      const selectedDate = moment(newValue[0]);
      if (selectedDate.isValid()) {
        const weekStart = selectedDate.clone().startOf("isoWeek");
        const weekEnd = weekStart.clone().add(4, "days"); // Monday to Friday

        const newRange: [Moment, Moment] = [weekStart, weekEnd];
        setValue(newRange);

        if (onChange && typeof onChange === "function") {
          // Maintain backward compatibility - pass the start date as before
          onChange(weekStart);
        }
      }
    }
  };

  // Disable dates based on campaign type and weekends
  const shouldDisableDate = (date: any) => {
    const dayMoment = moment(date);
    if (!dayMoment.isValid()) return true;

    const isWeekend = dayMoment.day() === 0 || dayMoment.day() === 6;

    if (isWeekend) return true;

    if (isEvergreenCampaign) {
      return dayMoment.isBefore(moment().add(7, "weeks").startOf("isoWeek"));
    }

    return dayMoment.isBefore(moment().startOf("isoWeek"));
  };

  // Calculate minimum date
  const minDate = isEvergreenCampaign
    ? moment().add(7, "weeks").startOf("isoWeek")
    : moment().startOf("isoWeek");

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DateRangePicker
        value={value}
        onChange={handleWeekChange}
        disabled={disableWeekSelection}
        shouldDisableDate={shouldDisableDate}
        minDate={minDate}
        format="MM/DD/YYYY"
        slotProps={{
          textField: {
            size: "small",
            fullWidth: true,
            padding: "0px",
            sx: {
              "& .MuiPickersOutlinedInput-root": { fontWeight: 500 },
            },
          } as any,
        }}
        onOpen={() => {
          // Use setTimeout to ensure the calendar is fully rendered before removing watermark
          setTimeout(() => {
            removeLicenseKeyWatermark();
          }, 100);
        }}
        slots={{
          openPickerIcon: InsertInvitationIcon,
        }}
        disablePast
        localeText={{
          start: "Week Start",
          end: "Week End",
        }}
      />
    </LocalizationProvider>
  );
};

export default CustomElements;
